import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;

    const min = parseInt(quantityInput.min) || 1;
    const max = parseInt(quantityInput.max) || null;
    const value = parseInt(quantityInput.value) || min;

    if (value < min) quantityInput.value = min;
    if (max && value > max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}

// ─── Stock limit popup & button state ────────────────────────────────────────
// Listens to the quantity-selector:update event (fired by the Web Component
// after every + / - click and blur) so we always have the latest value.
// This means:
//   - The + button re-enables as soon as qty drops below max (fixes "stays disabled")
//   - The popup only shows on an actual user action, never on page load (fixes top-left ghost)
//   - The - button click is covered because it also triggers quantity-selector:update

document.addEventListener('quantity-selector:update', (e) => {
  const { quantity, cartLine } = e.detail ?? {};

  // Find the selector that fired this event.
  // On the product page cartLine is 0/undefined; on the cart page it is 1-based.
  // e.target is the <input> that dispatched the event — walk up to the component.
  const input = e.target;
  if (!(input instanceof HTMLInputElement)) return;

  const selector = input.closest('quantity-selector-component');
  if (!selector) return;

  const plusBtn  = selector.querySelector('button[name="plus"]');
  const minusBtn = selector.querySelector('button[name="minus"]');
  const max      = parseInt(input.getAttribute('max'));
  const min      = parseInt(input.getAttribute('min')) || 1;

  // Always re-sync both buttons so - click also re-enables +
  if (plusBtn)  plusBtn.disabled  = Boolean(max && quantity >= max);
  if (minusBtn) minusBtn.disabled = quantity <= min;

  // Only show the popup when the user actually hits the ceiling
  if (max && quantity >= max) {
    showStockPopup(input, max);
  }
});

function showStockPopup(input, max) {
  let popup = document.getElementById('qty-stock-popup');

  if (!popup) {
    popup = document.createElement('div');
    popup.id = 'qty-stock-popup';
    popup.style.cssText = [
      'position:fixed',           // fixed so scroll offset never misplaces it
      'background:#fff1f1',
      'border:1px solid #d00',
      'color:#d00',
      'padding:6px 12px',
      'border-radius:4px',
      'font-size:0.85rem',
      'z-index:9999',
      'pointer-events:none',
      'box-shadow:0 2px 6px rgba(0,0,0,0.12)',
      'white-space:nowrap',
      'display:none',
    ].join(';');
    document.body.appendChild(popup);
  }

  popup.textContent = 'Only ' + max + ' available in stock';

  // Use getBoundingClientRect with position:fixed — no scroll offset needed
  const rect = input.getBoundingClientRect();
  popup.style.left = rect.left + 'px';
  popup.style.top  = (rect.bottom + 8) + 'px';
  popup.style.display = 'block';

  clearTimeout(popup._hideTimer);
  popup._hideTimer = setTimeout(() => { popup.style.display = 'none'; }, 2500);
}